/**
 * Live bridge for the onchain-activity widget. Renders NOTHING unless this
 * session has server streaming (`useServerStreaming`) AND a streaming client is
 * mounted — `useConnectionStatus` throws without one, and gating on the build
 * flag alone left the frames before the client exists (plan read, token
 * refresh) to crash. It does three things in one place: subscribe to the transactions
 * channel, lift the accumulated newest-first rows up to `useOnchainActivity`
 * (which merges them ahead of the fetched page), and render the shared
 * `LiveIndicator` pill — with a hover card describing what the feed is listening
 * to (region + watched addresses), an idea carried over from the legacy graph
 * live indicator.
 */
import * as React from "react";
import { useTransactionStream, type TxRow } from "@authe/tx-table";
import { useConnectionStatus, useOptionalStreamingClient, STREAM_STREAMED_TRANSACTIONS } from "@authe/streaming";
import { useServerStreaming } from "@/app/serverStreaming";
import { useCallerChannels } from "@/app/streamChannels";
import { LiveIndicator, liveStatusFromConnection } from "@authe/ui-core";
import { shortenAddress } from "@authe/fe-commons/chains";

export interface OnchainLiveFeedProps {
  enabled: boolean;
  onRows: (rows: TxRow[]) => void;
  /** New-since-last-look counter rendered on the pill. */
  count?: number;
  /** Addresses the feed is watching (rendered in the details hover card). */
  addresses?: string[];
}

export function OnchainLiveFeed(props: OnchainLiveFeedProps): React.ReactElement | null {
  const client = useOptionalStreamingClient();
  const { available } = useServerStreaming();
  if (!available || !client) return null;
  return <OnchainLiveFeedConnected {...props} />;
}

/** The feed proper — only ever mounted with a client in context. */
function OnchainLiveFeedConnected({
  enabled,
  onRows,
  count,
  addresses = [],
}: OnchainLiveFeedProps): React.ReactElement {
  const descriptors = useCallerChannels(STREAM_STREAMED_TRANSACTIONS);

  const { rows } = useTransactionStream({ channel: descriptors, enabled });
  const connection = useConnectionStatus(descriptors.map((c) => c.id));

  const onRowsRef = React.useRef(onRows);
  onRowsRef.current = onRows;
  React.useEffect(() => {
    onRowsRef.current(rows);
  }, [rows]);

  const details = (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Region</span>
        <span className="font-mono">{connection.region}</span>
      </div>
      <div>
        <span className="text-xs text-muted-foreground">
          {addresses.length > 0 ? "Watching addresses" : "Recent activity across the default chains"}
        </span>
        {addresses.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {addresses.slice(0, 8).map((a) => (
              <span
                key={a}
                className="rounded border border-border px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
              >
                {shortenAddress(a)}
              </span>
            ))}
            {addresses.length > 8 && (
              <span className="text-[11px] text-muted-foreground">+{addresses.length - 8} more</span>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <LiveIndicator
      status={enabled ? liveStatusFromConnection(connection.status) : "paused"}
      count={count}
      details={details}
      detailsTitle="Live blockchain feed"
    />
  );
}
